import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../../components/layout/Sidebar";
import Navbar from "../../components/layout/Navbar";
import { AuthContext } from "../../context/AuthContext";
import { getProcesses } from "../../services/reference.service";
import { 
  Plus, 
  Layers, 
  Settings2, 
  Edit3, 
  ExternalLink, 
  Search,
  ChevronRight,
  Loader2
} from "lucide-react";

export default function Processes() {
  const { user } = useContext(AuthContext);
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const loadProcesses = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token") || "";
        const data = await getProcesses(token);
        setProcesses(data);
      } catch (err) {
        console.error("Error cargando procesos", err);
      } finally {
        setLoading(false);
      }
    };
    loadProcesses();
  }, []);

  const filteredProcesses = processes.filter(p => 
    p.processName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.processCode.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      <Sidebar />

      <main className="flex-1 flex flex-col overflow-hidden">
        <Navbar />

        {/* HEADER */}
        <div className="px-8 py-6 bg-white border-b border-slate-200">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Layers className="w-4 h-4 text-blue-600" />
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Configuración</span>
              </div>
              <h2 className="text-3xl font-black text-slate-900 tracking-tight">
                Biblioteca de Procesos
              </h2>
            </div>
            
            <button
              onClick={() => navigate("/processes/create")}
              className="flex items-center justify-center gap-2 bg-slate-900 hover:bg-blue-600 text-white px-6 py-3 rounded-xl font-bold transition-all shadow-lg active:scale-95 text-sm"
            >
              <Plus className="w-5 h-5" /> Nuevo Proceso Maestro
            </button>
          </div>

          {/* Buscador Integrado */}
          <div className="mt-6 relative max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            <input 
              type="text" 
              placeholder="Buscar por código o nombre..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-11 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm"
            />
          </div>
        </div>

        {/* CONTENIDO */}
        <div className="flex-1 overflow-y-auto p-8">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-64">
              <Loader2 className="w-10 h-10 animate-spin text-blue-500 mb-4" />
              <p className="text-slate-400 font-medium animate-pulse">Sincronizando procesos...</p>
            </div>
          ) : filteredProcesses.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 bg-white rounded-[2rem] border-2 border-dashed border-slate-200 max-w-3xl mx-auto">
              <div className="bg-slate-50 p-6 rounded-full mb-6">
                <Layers className="w-12 h-12 text-slate-300" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">No hay procesos activos</h3>
              <p className="text-slate-500 text-center max-w-xs mb-8">
                Crea tu primer proceso maestro para empezar a asignar tareas a tu equipo de campo.
              </p>
              <button
                onClick={() => navigate("/processes/create")}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl font-bold transition-all shadow-xl shadow-blue-500/20"
              >
                Comenzar ahora
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 max-w-7xl mx-auto">
              {filteredProcesses.map((p) => (
                <div
                  key={p.id}
                  className="group bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-xl hover:shadow-slate-200/50 transition-all relative overflow-hidden"
                >
                  <div className="flex items-start justify-between relative z-10">
                    <div className="flex gap-4">
                      <div className="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                        <Settings2 className="w-6 h-6 text-slate-400 group-hover:text-white" />
                      </div>
                      <div>
                        <span className="text-[10px] font-black text-blue-600 bg-blue-50 px-2 py-0.5 rounded uppercase">
                          {p.processCode}
                        </span>
                        <h4 className="text-lg font-bold text-slate-900 mt-1">{p.processName}</h4>
                        <div className="flex items-center gap-4 mt-3">
                           <button
                            onClick={() => navigate(`/processes/${p.id}/activities`)}
                            className="flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-blue-600 transition-colors"
                          >
                            <Edit3 className="w-3.5 h-3.5" /> Gestionar Actividades
                          </button>
                           <div className="w-1 h-1 bg-slate-300 rounded-full"></div>
                           <button
                            onClick={() => navigate(`/processes/edit/${p.id}`)}
                            className="flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-slate-900 transition-colors"
                          >
                            <ExternalLink className="w-3.5 h-3.5" /> Editar Info
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    <button 
                      onClick={() => navigate(`/processes/${p.id}/activities`)}
                      className="p-2 bg-slate-50 rounded-lg text-slate-300 group-hover:text-blue-600 group-hover:bg-blue-50 transition-all"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                  
                  {/* Decoración de fondo de la card */}
                  <div className="absolute -right-4 -bottom-4 opacity-[0.02] group-hover:opacity-[0.05] transition-opacity">
                    <Layers className="w-32 h-32" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}